
import { ArrowRight, Zap } from 'lucide-react';
import { type PowerInfo, getPowerCategoryLabel } from '@/lib/powers-data';
import { getPowerCategoryBadgeStyle } from '@/lib/power-category-theme';
import { highlightSearchText } from '@/lib/search-highlight';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import { GsImage, ORBITAL_ICON_BOX_STYLE } from '@/components/help-center/latest-power-shared';

type PowerSearchResultRowProps = {
  power: PowerInfo;
  query: string;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
};

export default function PowerSearchResultRow({
  power,
  query,
  active,
  onSelect,
  onHover,
}: PowerSearchResultRowProps) {
  const badgeStyle = getPowerCategoryBadgeStyle(power.category);
  const displayName = formatPowerDisplayName(power.slug);

  return (
    <li className="border-b border-gray-50 last:border-b-0">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onSelect}
        onMouseEnter={onHover}
        className={`group flex w-full items-center gap-4 px-6 py-4 text-left transition-colors ${
          active ? 'bg-blue-50' : 'hover:bg-blue-50'
        }`}
      >
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl"
          style={ORBITAL_ICON_BOX_STYLE}
        >
          <GsImage
            code={power.slug}
            className="flex h-full w-full items-center justify-center"
            imgClassName="max-h-9 max-w-9 object-contain"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <Zap className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" aria-hidden />
            <span className="line-clamp-1 font-poppins text-sm font-semibold text-gray-900">
              {highlightSearchText(displayName, query)}
            </span>
          </div>
          <span
            className="inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold"
            style={badgeStyle}
          >
            {getPowerCategoryLabel(power.category)}
          </span>
        </div>
        <ArrowRight
          className={`h-4 w-4 shrink-0 transition-all ${
            active
              ? 'translate-x-0.5 text-blue-500'
              : 'text-gray-300 group-hover:translate-x-0.5 group-hover:text-blue-400'
          }`}
        />
      </button>
    </li>
  );
}
